export default {
  dreamCodeHero: {
    label: 'PROGRAM',
    title: 'DREAM CODE',
    subtitle: 'An educational framework that designs growth from structure',
  },
  whyChange: {
    label: 'WHY CHANGE',
    title: 'Why can people change?',
    body1: 'The reason many people wish to "change" but the change does not last is not weakness of will, but "structure".',
    body2: 'Thoughts generate actions, and actions generate results.\nHowever, those thoughts and actions are influenced by cognitive patterns formed through past experiences.',
    highlight: 'DREAM CODE is an educational process that visualizes and redesigns that unconscious structure.\nIt aims not for temporary elation, but for reproducible change.',
  },
  cycle: {
    label: 'CYCLE STRUCTURE',
    title: 'A 4-stage circular design\nthat establishes change',
    stage1: { number: '01', title: 'Foundation', subtitle: 'Self-understanding and structural recognition', desc: 'Objectively view your thought patterns and behavioral tendencies, and clarify unconscious assumptions.' },
    stage2: { number: '02', title: 'Reconstruction', subtitle: 'Redesign of thought circuits', desc: 'Form new cognitive patterns through repeated practice and introspection.' },
    stage3: { number: '03', title: 'Future Design', subtitle: 'Strategic vision building', desc: 'Concretize the desired future image and implement it as an action plan.' },
    stage4: { number: '04', title: 'Autonomous Development', subtitle: 'Establishment of sustainable growth', desc: 'Circulate learning and transition to a state of autonomous development.' },
    centerLabel: 'CYCLE',
    centerText: 'Circular Design',
    footer: 'Learn, experience, reflect, establish.\nThis circulation creates sustainable change.',
  },
  scientific: {
    label: 'SCIENTIFIC BASIS',
    title: 'Educational design based on scientific knowledge',
    keyword1: 'Neuroplasticity',
    keyword2: "Hebb's Law",
    keyword3: 'Cognitive Behavioral Theory',
    keyword4: 'Self-Efficacy Theory',
    body1: 'DREAM CODE does not rely on a single specific theory.',
    body2: 'It draws educational design insights from multiple academic findings, including neuroplasticity, Hebb\'s law, cognitive behavioral theory, and self-efficacy theory.',
    body3: 'It does not claim that thoughts directly change physical phenomena.\nHowever, many studies have shown that thoughts influence attention, choice, and action.',
    conclusion1: 'If structure changes,\nbehavior changes.',
    conclusion2: 'If behavior changes,\nresults change.',
    conclusion3: 'We design this chain as education.',
  },
  integration: {
    label: 'INTEGRATION',
    title: 'If you change the structure, the future changes.',
    body1: 'Change is not accidental.',
    body2: 'By understanding principles and establishing them through experience, reproducible growth is born.',
    body3: 'DREAM CODE is an educational framework that presents that structure.',
  },
  cta: {
    button1: 'Consider individual participation',
    button2: 'Consult about implementation',
  },
  fourFields: {
    label: 'FOUR FIELDS',
    title: 'Integration from four academic perspectives',
    philosophy: {
      label: 'Success Philosophy',
      labelEn: 'SUCCESS PHILOSOPHY',
      goal: 'Development Goal: Success Brain (Reality and Truth Brain)',
      goalEn: 'Architectural Intelligence',
      description: 'Success philosophy is a field that systematically organizes the thinking principles that produce results. Based on historical thinkers and modern research, it focuses on "thinking habits that produce results". It deals with success as a principle, not emotional theory.',
    },
    brain: {
      label: 'Basic Brain Science',
      labelEn: 'BRAIN SCIENCE',
      goal: 'Development Goal: Personality Brain (Repetition Brain, Conversation Brain, Leader Brain)',
      goalEn: 'Character Intelligence',
      description: 'Neuroplasticity and Hebb\'s law show that neural circuits are strengthened through experience and repetition. DREAM CODE promotes the formation of new thought circuits by combining repetition and introspection.',
    },
    psychology: {
      label: 'Behavioral Psychology',
      labelEn: 'BEHAVIORAL PSYCHOLOGY',
      goal: 'Development Goal: Patience Brain (Loved Brain, Human Relations Brain)',
      goalEn: 'Relational Intelligence',
      description: 'Cognitive behavioral theory shows that "interpretation", not events, determines behavior. Beliefs influence choices, and choices direct results. It emphasizes practical design at the behavioral level.',
    },
    quantum: {
      label: 'Basic Quantum Mechanics',
      labelEn: 'QUANTUM MECHANICS',
      goal: 'Development Goal: Emotional Brain (Emotional Management Brain, Goal Achievement Brain, Asset Formation Brain)',
      goalEn: 'Emotional Intelligence',
      description: 'Quantum mechanics is a discipline that deals with physical phenomena and does not directly explain human wish fulfillment. However, the concepts of "observation", "uncertainty", and "possibility" provide a perspective to reconsider the structure of reality perception. DREAM CODE positions these as philosophical and conceptual frameworks.',
    },
    courses: {
      '01': { title: 'Law of Reality and Truth', subtitle: '100% Dream Realization! Theory of Imagination and Choice' },
      '02': { title: 'Law of Dream Realization', subtitle: 'Theory of 3 Principles of Success' },
      '03': { title: 'Law of Self-Image', subtitle: 'Theory of Self-Image Completion' },
      '04': { title: 'Law of Psycho-Cybernetics', subtitle: 'Theory of Goal Pursuit Device' },
      '05': { title: 'Law of Belief', subtitle: 'Theory of Beliefs Common to 4 Great Figures' },
      '06': { title: 'Law of Assumptions', subtitle: 'Placebo-Nocebo Effect Theory' },
      '07': { title: '5 Laws of Winners and Losers', subtitle: 'Theory of Winner and Loser Habit Brain' },
      '08': { title: '5-Stage Law of Practical Belief Development', subtitle: 'Complete Belief Mastery Theory' },
      '09': { title: 'Law of Conscious and Subconscious Mind', subtitle: 'Conscious × Subconscious × RAS Theory' },
      '10': { title: 'Law of Seriousness', subtitle: 'Socratic Philosophy Theory' },
      '11': { title: 'Law of Paradigm Shift', subtitle: 'Theory of Escape from Self-Centeredness' },
      '12': { title: 'Law of Practice and Prohibitions', subtitle: '6 Theories for Daily Life Application' },
      '13': { title: 'Law of Stage Fright Liberation', subtitle: 'Brain Neuron Signal Transmission Theory' },
      '14': { title: 'Law of Self-Reward Neural Groups', subtitle: 'Maslow\'s Hierarchy of Needs Theory' },
      '15': { title: 'Law of 10,000 Hours', subtitle: 'Brain Circuit Development Theory through Repetition' },
      '16': { title: 'Law of 3 Major Neurotransmitters', subtitle: 'Absolutely Essential Chemical Substance Theory' },
      '17': { title: 'Law of 7 Powers (Ability)', subtitle: '4-Stage Success Brain Circuit Development Theory' },
      '18': { title: 'Law of Communication', subtitle: 'Ultimate Conversation Technique Theory' },
      '19': { title: '5-Stage Law of Objection Handling', subtitle: 'Complaint Handling Method Theory' },
      '20': { title: 'Law of Brain Chemicals', subtitle: 'Gambling & Romance Handling Theory' },
      '21': { title: '3-Principle Law of Trust Acquisition', subtitle: 'Ultimate Subordinate Development Theory' },
      '22': { title: 'Law of Strongest Organization Development', subtitle: 'Ultimate Organization Development Technique Theory' },
      '23': { title: 'Law of Stress Control', subtitle: 'Stress Handling Method Theory' },
      '24': { title: 'Law of Total Responsibility', subtitle: 'What-If Prohibition Theory' },
      '25': { title: '4-Stage Law of Love', subtitle: '4 Principles of Personality Formation Theory' },
      '26': { title: 'Law of "Loved Brain Circuit"', subtitle: 'Want to be Recognized/Loved Theory' },
      '27': { title: 'Law of Instinctive and Intentional Desires', subtitle: 'Professional and Amateur Theory' },
      '28': { title: 'Ultimate Law of Motivation', subtitle: 'How to Get and Give Motivation Theory' },
      '29': { title: 'Law of Fear and Anger', subtitle: 'Liberation from Ego Theory' },
      '30': { title: 'Ultimate Golden Rule Law', subtitle: 'Principle of Giving Theory' },
      '31': { title: 'Ultimate Law of Decision', subtitle: 'Freedom of Choice Theory' },
      '32': { title: 'Law of Successful Values', subtitle: 'Act-As-If Theory' },
      '33': { title: 'Law of Great Figures\' Secrets', subtitle: '3, 8, 18 Years Old Theory' },
      '34': { title: 'Broken Windows Theory Law', subtitle: 'Mind-Body Relationship Theory' },
      '35': { title: 'Law of Escape from Dependency Brain', subtitle: 'Self-Establishment Brain Circuit Development Theory' },
      '36': { title: 'Law of Dementia Improvement Brain Circuit', subtitle: 'Center Method Brain Circuit Development Theory' },
      '37': { title: 'Law of Quantum Medicine', subtitle: 'Science × Philosophy × Wave Resonance × Human Consciousness × AI Analysis Theory' },
      '38': { title: 'Law of Wave Resonance', subtitle: 'Joy, Prayer, Gratitude Brain Circuit Theory' },
      '39': { title: 'Law of Quantum Field', subtitle: 'Royal Raymond Rife, Ph.D. Cancer Cell Removal Ray Machine Theory' },
      '40': { title: 'Law of Quantum Medicine × Wave × AI', subtitle: 'Future Medical Theory' },
      '41': { title: 'Law of Creative Selling', subtitle: 'Sales Brain Circuit Development Theory' },
      '42': { title: '4-Stage Law of Success Brain Circuit Development', subtitle: 'Sports Brain/Health Brain Circuit Development Theory' },
      '43': { title: 'Law of Wave Declaration × AI', subtitle: 'Affirmation Theory' },
      '44': { title: 'Law of Wave Video × AI', subtitle: 'Visualization Theory' },
      '45': { title: '3 Laws of Money Philosophy', subtitle: 'Millionaire Practice 1 Savings Theory' },
      '46': { title: 'Law of 10x Asset Formation', subtitle: '3 Principles of Asset Formation Theory' },
      '47': { title: 'Law of Time = Money = Effort', subtitle: 'Waste, Consumption, Investment Theory' },
      '48': { title: 'Law of Perpetual Success Giving', subtitle: 'Complete Personality Formation Theory' },
    },
  },
};